"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.groupUsedLockupsByKind = exports.getVsrDepositAmount = exports.getVsrDepositEntry = exports.getVsrMintConfigIdx = exports.getVsrVoterData = void 0;
const sdk_1 = require("./sdk");
const enums_1 = require("./enums");
const anchor_1 = require("@coral-xyz/anchor");
/**
 * Search VSR mango plugin for Voter account data.
 * @returns {Voter} - Returns Voter Data
 */
async function getVsrVoterData({ vsrProgram, realm, mint, authority, }) {
    const registrar = (0, sdk_1.registrarAddress)({
        programId: vsrProgram.programId,
        realm,
        mint,
    })[0];
    const voter = (0, sdk_1.voterAddress)({
        programId: vsrProgram.programId,
        registrar,
        voterAuthority: authority,
    })[0];
    const voterData = await vsrProgram.account.voter.fetchNullable(voter);
    if (voterData === null) {
        throw new Error(`Voter data for ${authority.toBase58()} does not exist ` +
            `(VSR program id: ${vsrProgram.programId.toBase58()}, realm: ${realm.toBase58()}, mint: ${mint.toBase58()}})`);
    }
    return voterData;
}
exports.getVsrVoterData = getVsrVoterData;
/**
 * Search VSR mango plugin for VsrMintConfigIdx
 * @returns {number} - Returns VsrMintConfigIdx for a given mint
 */
async function getVsrMintConfigIdx({ vsrProgram, realm, mint, filter, }) {
    const registrar = (0, sdk_1.registrarAddress)({
        programId: vsrProgram.programId,
        realm,
        mint,
    })[0];
    const registrarData = await vsrProgram.account.registrar.fetchNullable(registrar);
    if (registrarData === null) {
        throw new Error(`Registrar account ${registrar.toBase58()} does not exist ` +
            `(VSR program id: ${vsrProgram.programId.toBase58()}, realm: ${realm.toBase58()}, mint: ${mint.toBase58()}})`);
    }
    const registrarVotingMints = registrarData.votingMints;
    let vsrMintConfigIdx = undefined;
    for (let i = 0; i < registrarVotingMints.length; i++) {
        if (registrarVotingMints[i].mint.equals(filter.mint)) {
            vsrMintConfigIdx = i;
            break;
        }
    }
    if (vsrMintConfigIdx === undefined) {
        throw new Error(`Mint ${filter.mint.toBase58()} is not configured in registrar ${registrar.toBase58()} ' +
      'at realm ${registrarData.realm.toBase58()}`);
    }
    return vsrMintConfigIdx;
}
exports.getVsrMintConfigIdx = getVsrMintConfigIdx;
/**
 * Search VSR mango plugin for Voter account deposit entry.
 * @returns {VsrDepositEntryResult} - Returns type 'Existing' when entry that matches the filter is found,
 *                                    type 'EmptyEntryAt' when not proprietary entry is found but there is empty place to create new,
 *                                    type 'NoVoterAccount' when no `Voter` account exists
 *                                    and type 'NoDepositEntryAvailable' when all entries are occupied no one is matching the filter.
 */
async function getVsrDepositEntry({ vsrProgram, realm, mint, authority, filter, }) {
    const voterData = await getVsrVoterData({
        vsrProgram,
        realm,
        mint,
        authority,
    });
    const vsrMintConfigIdx = await getVsrMintConfigIdx({
        vsrProgram,
        realm,
        mint,
        filter,
    });
    const deposits = voterData.deposits;
    for (let i = 0; i < deposits.length; i++) {
        const { lockup, votingMintConfigIdx, allowClawback, isUsed } = deposits[i];
        if (votingMintConfigIdx === vsrMintConfigIdx &&
            filter.lockupKind === (0, enums_1.lockupKind)(lockup.kind) &&
            lockup.endTs.sub(lockup.startTs).eqn(filter.lockupPeriod) &&
            (filter.allowClawback === null ||
                filter.allowClawback === allowClawback) &&
            isUsed) {
            return { type: 'Existing', index: i };
        }
        if (!isUsed) {
            // the first available index that's not used will be taken
            return { type: 'EmptyEntryAt', index: i };
        }
    }
    // neither found existing entry with matching lockup type, nor non-used non-claimable entry
    return { type: 'NoDepositEntryAvailable', depositEntries: deposits };
}
exports.getVsrDepositEntry = getVsrDepositEntry;
/**
 * Search VSR mango plugin for Voter account locked funds.
 * @returns {BN} - Returns user's locked funds
 */
async function getVsrDepositAmount({ vsrProgram, realm, mint, authority, }) {
    const voterData = await getVsrVoterData({
        vsrProgram,
        realm,
        mint,
        authority,
    });
    let lockedAmount = new anchor_1.BN(0);
    const deposits = voterData.deposits;
    for (let i = 0; i < deposits.length; i++) {
        const { lockup, amountDepositedNative, isUsed } = deposits[i];
        if ((0, enums_1.lockupKind)(lockup.kind) !== enums_1.LockupKindEnum.None && isUsed) {
            lockedAmount = lockedAmount.add(amountDepositedNative);
        }
    }
    return lockedAmount;
}
exports.getVsrDepositAmount = getVsrDepositAmount;
function groupUsedLockupsByKind(depositEntries, filterOutWithAllowClawback = true) {
    const groupedItems = {};
    for (let i = 0; i < depositEntries.length; i++) {
        const { lockup, votingMintConfigIdx: mintIndex, isUsed, allowClawback, } = depositEntries[i];
        if (!isUsed) {
            // when not used, then not initialized (is closed) and there is nothing we can do with it later
            continue;
        }
        if (filterOutWithAllowClawback && allowClawback) {
            // we don't want to work with lockups that can be clawed back
            continue;
        }
        const duration = lockup.endTs.sub(lockup.startTs);
        const kind = (0, enums_1.lockupKind)(lockup.kind);
        const key = `${mintIndex}_${kind}_${duration}`;
        if (!groupedItems[key]) {
            groupedItems[key] = [i];
        }
        else {
            groupedItems[key].push(i);
        }
    }
    return Object.values(groupedItems);
}
exports.groupUsedLockupsByKind = groupUsedLockupsByKind;
//# sourceMappingURL=vsrDepositInfo.js.map